import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'

import { createSection, updateSection } from '../../actions/certificationActions'
import { reorderSections } from '../../reducers/certificationBuilderReducer'
import { curriculumTotals, showGenericError } from '../../utility/certificationBuilder'
import { useBuilderSensors, dragEndIds } from './sortable'
import SectionCard from './SectionCard'
import FinalQuizCard from './FinalQuizCard'

import classes from './CurriculumTree.module.scss'

const CurriculumTree = ({ certification, selection, onSelect }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const sensors = useBuilderSensors()
  const totals = curriculumTotals(certification.sections)

  const handleDragEnd = (event) => {
    const ids = dragEndIds(event)
    if (!ids) { return }

    dispatch(reorderSections(ids))
    const position = certification.sections.findIndex(section => section.id === ids.overId) + 1
    dispatch(updateSection({ certificationId: certification.id, id: ids.activeId, position })).unwrap().catch(showGenericError)
  }

  const addSection = () => dispatch(createSection({ certificationId: certification.id, title: '' })).unwrap().catch(showGenericError)

  return (
    <div className='d-flex flex-column'>
      <div className={classNames('d-flex align-items-baseline justify-content-between', classes.header)}>
        <span className={classes.eyebrow}>
          {t('certificationBuilder.curriculum.title')}
          {' · '}
          {t('certificationBuilder.curriculum.totals', { sections: totals.sections, lessons: totals.lessons })}
        </span>
        <span className={classes.hint}>
          <i className='fa fa-bars mx-1' aria-hidden='true' />
          {t('certificationBuilder.curriculum.dragHint')}
        </span>
      </div>

      {certification.sections.length === 0 && (
        <div className={classNames('text-center', classes.empty)}>
          <div className={classes.emptyTitle}>{t('certificationBuilder.curriculum.emptyTitle')}</div>
          <p className={classes.emptyBody}>{t('certificationBuilder.curriculum.emptyBody')}</p>
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={certification.sections.map(section => section.id)} strategy={verticalListSortingStrategy}>
          {certification.sections.map((section, index) => (
            <SectionCard
              key={section.id}
              certificationId={certification.id}
              section={section}
              number={index + 1}
              selection={selection}
              onSelect={onSelect}
            />
          ))}
        </SortableContext>
      </DndContext>

      <button className={classes.addSection} onClick={addSection}>
        <i className='fa fa-plus mr-2' aria-hidden='true' />
        {t('certificationBuilder.curriculum.addSection')}
      </button>

      <FinalQuizCard
        finalQuiz={certification.finalQuiz}
        selected={selection?.type === 'finalQuiz'}
        onSelect={() => onSelect({ type: 'finalQuiz' })}
      />
    </div>
  )
}

CurriculumTree.propTypes = {
  certification: PropTypes.shape({
    id: PropTypes.number.isRequired,
    sections: PropTypes.array.isRequired,
    finalQuiz: PropTypes.object.isRequired,
  }).isRequired,
  selection: PropTypes.shape({ type: PropTypes.string.isRequired }),
  onSelect: PropTypes.func.isRequired,
}

export default CurriculumTree
